const MIN_RELATIVE_COLUMN_WIDTH = 50;

export type ColumnSizingState = Record<string, number>;

interface ColumnSizingStorageContext {
  tableId: string;
  agendaId: string;
  viewId: string;
  queryKey: string;
}

const STORAGE_KEY_PREFIX = 'table-column-sizing:';

const isColumnSizingState = (value: unknown): value is ColumnSizingState => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;

  return Object.values(value).every((size) => typeof size === 'number' && Number.isFinite(size) && size > 0);
};

export const getColumnSizingStorageKey = ({ tableId, agendaId, viewId, queryKey }: ColumnSizingStorageContext): string =>
  `${STORAGE_KEY_PREFIX}${[tableId, agendaId, viewId, queryKey].map((value) => encodeURIComponent(value || 'default')).join(':')}`;

export const readColumnSizing = (storageKey: string | null): ColumnSizingState => {
  if (typeof window === 'undefined' || !storageKey) return {};

  try {
    const storedValue = window.localStorage.getItem(storageKey);
    if (!storedValue) return {};

    const parsedValue: unknown = JSON.parse(storedValue);
    return isColumnSizingState(parsedValue) ? parsedValue : {};
  } catch {
    return {};
  }
};

export const writeColumnSizing = (storageKey: string | null, columnSizing: ColumnSizingState): void => {
  if (typeof window === 'undefined' || !storageKey) return;

  try {
    if (Object.keys(columnSizing).length === 0) {
      window.localStorage.removeItem(storageKey);
      return;
    }

    window.localStorage.setItem(storageKey, JSON.stringify(columnSizing));
  } catch {
    // Storage can be unavailable or full. Column resizing must still work in memory.
  }
};

export const resetColumnSizing = (columnSizing: ColumnSizingState, columnId: string): ColumnSizingState => {
  const nextColumnSizing = { ...columnSizing };
  delete nextColumnSizing[columnId];
  return nextColumnSizing;
};

export const resetAllColumnSizing = (): ColumnSizingState => ({});

export const removeDefaultColumnSizing = (columnSizing: ColumnSizingState, defaultColumnSize: number): ColumnSizingState =>
  Object.fromEntries(Object.entries(columnSizing).filter(([, size]) => size !== defaultColumnSize));

export const fitColumnWidths = (
  widths: ColumnSizingState,
  containerWidth: number,
  auxiliaryWidth: number,
  relativeWeights: ColumnSizingState = {}
) => {
  const relative = Object.entries(relativeWeights).filter(([id, weight]) => id in widths && weight > 0);
  if (containerWidth <= 0 || !relative.length) return widths;
  const relativeIds = new Set(relative.map(([id]) => id));
  const reserved = Object.entries(widths).reduce((sum, [id, width]) => sum + (relativeIds.has(id) ? 0 : width), auxiliaryWidth);
  const weightSum = relative.reduce((sum, [, weight]) => sum + weight, 0);
  const smallestWeight = relative.reduce((smallest, [, weight]) => Math.min(smallest, weight), Infinity);
  const scale = Math.max((containerWidth - reserved) / weightSum, MIN_RELATIVE_COLUMN_WIDTH / smallestWeight);
  return { ...widths, ...Object.fromEntries(relative.map(([id, weight]) => [id, weight * scale])) };
};

export const getColumnSizingChanges = (next: ColumnSizingState, rendered: ColumnSizingState) =>
  Object.fromEntries(Object.entries(next).filter(([id, width]) => Math.abs(width - rendered[id]) >= 0.5));
